import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { X } from 'lucide-react';
import { useResolveIcalConflict } from '../../hooks/useICalConflicts';
import { ICalConflict, IcalConflictActionError, IcalConflictActionErrorCode } from '../../types/icalConflict';

interface ConflictResolveDialogProps {
  readonly conflict: ICalConflict;
  readonly onClose: () => void;
  readonly onResolved: () => void;
}

type Step = 'choose' | 'confirmCancel' | 'confirmKeep';

/** Maps a resolve-conflict action error to a distinct, owner-facing message (FR-008/FR-012). */
const errorMessageForCode = (
  code: IcalConflictActionErrorCode,
  t: TFunction<readonly ['common', 'pages', 'errors'], undefined>
): string => {
  switch (code) {
    case 'ALREADY_RESOLVED':
      return t('errors:icalConflictAlreadyResolved');
    case 'NOT_FOUND':
      return t('errors:icalConflictNotFound');
    case 'FORBIDDEN':
      return t('errors:icalConflictForbidden');
    default:
      return t('errors:somethingWentWrong');
  }
};

/**
 * Resolve dialog for a `DOUBLE_BOOKING` conflict (US2 cancel-outcome, US3 keep-outcome).
 * Both outcomes require an explicit confirmation step before any mutation fires — the cancel path
 * warns about guest notification (a real, irreversible side effect once wired to a live backend);
 * the keep path explains the external side remains the owner's responsibility. Reuses this app's
 * existing destructive-confirmation dialog pattern/copy conventions (see `CancelBookingDialog.tsx`)
 * rather than inventing a new cancellation UI.
 */
export const ConflictResolveDialog = ({ conflict, onClose, onResolved }: ConflictResolveDialogProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors']);
  const [step, setStep] = useState<Step>('choose');
  const [error, setError] = useState<string | null>(null);
  const resolveMutation = useResolveIcalConflict();

  const isPending = resolveMutation.isPending;

  const handleClose = () => {
    setStep('choose');
    setError(null);
    onClose();
  };

  const submit = async (outcome: 'CANCEL_PLATFORM_BOOKING' | 'DISMISS') => {
    setError(null);
    try {
      await resolveMutation.mutateAsync({
        apartmentId: conflict.apartmentId,
        conflictId: conflict.conflictId,
        request: { outcome },
      });
      onResolved();
    } catch (err) {
      const typedError = err as IcalConflictActionError;
      setError(errorMessageForCode(typedError.code, t));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="conflict-resolve-dialog">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('pages:resolveConflictHeading')}</h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label={t('common:dismiss')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {step === 'choose' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{t('pages:resolveConflictChooseOutcome')}</p>
              <button
                type="button"
                onClick={() => setStep('confirmCancel')}
                className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="conflict-outcome-cancel"
              >
                <span className="block font-medium text-gray-900">
                  {t('pages:conflictOutcomeCancelAction')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setStep('confirmKeep')}
                className="w-full text-left px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="conflict-outcome-keep"
              >
                <span className="block font-medium text-gray-900">
                  {t('pages:conflictOutcomeKeepAction')}
                </span>
              </button>
            </div>
          )}

          {step === 'confirmCancel' && (
            <div className="space-y-3">
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="conflict-cancel-warning">
                {t('pages:conflictCancelGuestNotificationWarning')}
              </p>
            </div>
          )}

          {step === 'confirmKeep' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg" data-testid="conflict-keep-explanation">
                {t('pages:conflictKeepResponsibilityExplanation')}
              </p>
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          {step === 'choose' ? (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common:dismiss')}
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setStep('choose');
                  setError(null);
                }}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('common:back')}
              </button>
              <button
                onClick={() => submit(step === 'confirmCancel' ? 'CANCEL_PLATFORM_BOOKING' : 'DISMISS')}
                disabled={isPending}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  step === 'confirmCancel'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
                data-testid="conflict-confirm-submit"
              >
                {isPending && (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {step === 'confirmCancel'
                  ? t('pages:confirmCancelPlatformBooking')
                  : t('pages:confirmKeepPlatformBooking')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
